/**
 * Game Preset Definitions for Role Roulette
 *
 * Provides pre-configured character presets for popular games like Avalon and Mafia.
 * Each character includes default abilities that can be customized by users.
 */

export interface CharacterPreset {
  id: string; // Unique identifier: "avalon_merlin", "mafia_police"
  name_ko: string; // Korean name
  name_en: string; // English name
  description_ko: string; // Character description in Korean
  description_en: string; // Character description in English
  faction?: string; // "good", "evil", "mafia", "citizen"
  defaultAbilities: string[]; // Default ability IDs (matches abilities.ts)
  icon?: string; // Optional emoji/icon
}

export interface GamePresetCategory {
  id: string; // "avalon", "mafia"
  name_ko: string;
  name_en: string;
  characters: CharacterPreset[];
  defaultRole: CharacterPreset; // Default/basic role for the game (e.g., 시민, 충신)
}

/**
 * Avalon Character Presets
 */
const avalonCharacters: CharacterPreset[] = [
  // Good Faction
  {
    id: "avalon_merlin",
    name_ko: "멀린",
    name_en: "Merlin",
    description_ko:
      "모드레드를 제외한 악의 세력을 알고 있지만, 정체가 드러나면 암살당합니다.",
    description_en:
      "Knows the evil players but can be assassinated if revealed.",
    faction: "good",
    defaultAbilities: [
      "see_roles",
      "nominate",
      "vote_choice",
      "secret_vote_choice",
    ],
    icon: "🔮",
  },
  {
    id: "avalon_percival",
    name_ko: "퍼시벌",
    name_en: "Percival",
    description_ko:
      "멀린과 모르가나를 볼 수 있지만, 둘 중 누가 진짜 멀린인지 알 수 없습니다.",
    description_en: "Sees Merlin and Morgana but doesn't know which is which.",
    faction: "good",
    defaultAbilities: [
      "see_roles",
      "nominate",
      "vote_choice",
      "secret_vote_choice",
    ],
    icon: "🛡️",
  },
  {
    id: "avalon_loyal_servant",
    name_ko: "충신",
    name_en: "Loyal Servant",
    description_ko: "특별한 능력이 없는 선의 세력입니다.",
    description_en: "A good player with no special abilities.",
    faction: "good",
    defaultAbilities: ["nominate", "vote_choice", "secret_vote_choice"],
    icon: "⚔️",
  },
  // Evil Faction
  {
    id: "avalon_mordred",
    name_ko: "모드레드",
    name_en: "Mordred",
    description_ko: "멀린에게 보이지 않는 악의 세력입니다.",
    description_en: "An evil player invisible to Merlin.",
    faction: "evil",
    defaultAbilities: [
      "see_roles",
      "nominate",
      "vote_choice",
      "secret_vote_choice",
    ],
    icon: "🗡️",
  },
  {
    id: "avalon_morgana",
    name_ko: "모르가나",
    name_en: "Morgana",
    description_ko: "퍼시벌에게 멀린처럼 보이는 악의 세력입니다.",
    description_en: "Appears as Merlin to Percival.",
    faction: "evil",
    defaultAbilities: [
      "see_roles",
      "nominate",
      "vote_choice",
      "secret_vote_choice",
    ],
    icon: "🔮",
  },
  {
    id: "avalon_assassin",
    name_ko: "암살자",
    name_en: "Assassin",
    description_ko: "게임 종료 시 멀린을 암살할 수 있는 악의 세력입니다.",
    description_en: "Can assassinate Merlin at the end of the game.",
    faction: "evil",
    defaultAbilities: [
      "see_roles",
      "assassinate",
      "nominate",
      "vote_choice",
      "secret_vote_choice",
    ],
    icon: "🗡️",
  },
  {
    id: "avalon_oberon",
    name_ko: "오베론",
    name_en: "Oberon",
    description_ko:
      "다른 악의 세력을 알 수 없고, 다른 악의 세력도 오베론을 알 수 없습니다.",
    description_en: "Unknown to other evil players and doesn't know them.",
    faction: "evil",
    defaultAbilities: ["nominate", "vote_choice", "secret_vote_choice"],
    icon: "👤",
  },
  {
    id: "avalon_minion",
    name_ko: "미니언",
    name_en: "Minion of Mordred",
    description_ko: "특별한 능력이 없는 악의 세력입니다.",
    description_en: "An evil player with no special abilities.",
    faction: "evil",
    defaultAbilities: [
      "see_roles",
      "nominate",
      "vote_choice",
      "secret_vote_choice",
    ],
    icon: "⚔️",
  },
];

/**
 * Mafia Character Presets
 */
const mafiaCharacters: CharacterPreset[] = [
  {
    id: "mafia_mafia",
    name_ko: "마피아",
    name_en: "Mafia",
    description_ko: "밤에 시민을 제거할 수 있는 악의 세력입니다.",
    description_en: "Evil player who can eliminate citizens at night.",
    faction: "mafia",
    defaultAbilities: ["assassinate", "nominate", "vote_choice"],
    icon: "🔫",
  },
  {
    id: "mafia_citizen",
    name_ko: "시민",
    name_en: "Citizen",
    description_ko: "특별한 능력이 없는 선량한 시민입니다.",
    description_en: "A good player with no special abilities.",
    faction: "citizen",
    defaultAbilities: ["nominate", "vote_choice"],
    icon: "👤",
  },
  {
    id: "mafia_police",
    name_ko: "경찰",
    name_en: "Police",
    description_ko: "밤에 한 명의 정체를 조사할 수 있습니다.",
    description_en: "Can investigate one player's identity at night.",
    faction: "citizen",
    defaultAbilities: ["investigate", "nominate", "vote_choice"],
    icon: "👮",
  },
  {
    id: "mafia_doctor",
    name_ko: "의사",
    name_en: "Doctor",
    description_ko: "밤에 한 명을 마피아의 공격으로부터 보호할 수 있습니다.",
    description_en: "Can protect one player from mafia attacks at night.",
    faction: "citizen",
    defaultAbilities: ["protect", "nominate", "vote_choice"],
    icon: "💉",
  },
];

/**
 * Default roles for each game
 */
const avalonDefaultRole: CharacterPreset = {
  id: "avalon_loyal_servant",
  name_ko: "충신",
  name_en: "Loyal Servant",
  description_ko: "특별한 능력이 없는 선의 세력입니다.",
  description_en: "A good player with no special abilities.",
  faction: "good",
  defaultAbilities: ["nominate", "vote_choice", "secret_vote_choice"],
  icon: "⚔️",
};

const mafiaDefaultRole: CharacterPreset = {
  id: "mafia_citizen",
  name_ko: "시민",
  name_en: "Citizen",
  description_ko: "특별한 능력이 없는 선량한 시민입니다.",
  description_en: "A good player with no special abilities.",
  faction: "citizen",
  defaultAbilities: ["nominate", "vote_choice"],
  icon: "👤",
};

/**
 * All Game Presets
 */
export const GAME_PRESETS: GamePresetCategory[] = [
  {
    id: "avalon",
    name_ko: "아발론",
    name_en: "Avalon",
    characters: avalonCharacters,
    defaultRole: avalonDefaultRole,
  },
  {
    id: "mafia",
    name_ko: "마피아",
    name_en: "Mafia",
    characters: mafiaCharacters,
    defaultRole: mafiaDefaultRole,
  },
];

/**
 * Helper function to get a character preset by ID
 */
export function getCharacterPresetById(
  id: string
): CharacterPreset | undefined {
  for (const game of GAME_PRESETS) {
    const character = game.characters.find((c) => c.id === id);
    if (character) return character;
  }
  return undefined;
}

/**
 * Helper function to get all characters for a specific game
 */
export function getCharactersByGame(gameId: string): CharacterPreset[] {
  const game = GAME_PRESETS.find((g) => g.id === gameId);
  return game ? game.characters : [];
}
